import { Controller, useForm } from 'react-hook-form';
import Modal from 'react-modal';
// material ui
import { Box, Button, Divider, FormHelperText, Grid, Stack, TextField, Typography } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';

// graphql
import { useUpdateCashierPasswordMutation } from '@/generated/graphql';

// interfaces
import type { ICashierTable, IEditCashierPasswordInput } from '@/shared/interfaces';

import { MdOutlinePostAdd } from 'react-icons/md';
import { useEffect, useState } from 'react';

interface IUsherProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  data: ICashierTable | undefined;
  refetch: () => void;
}

export const ModalEditCashierPassword = ({ open, setOpen, data, refetch }: IUsherProps) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const handleClose = () => setOpen(false);

  const { reset, handleSubmit, control } = useForm<IEditCashierPasswordInput>({
    mode: 'onChange',
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const [updateCashierPassword] = useUpdateCashierPasswordMutation();

  const onSubmit = async (dataInput: IEditCashierPasswordInput) => {
    const { confirmPassword, newPassword } = dataInput;

    setErrorMessage('');

    if (confirmPassword !== newPassword) {
      setErrorMessage('Password does not match');
    } else if (data && confirmPassword === newPassword) {
      await updateCashierPassword({
        variables: {
          input: {
            password: newPassword,
            cashierId: data.id,
          },
        },
      });

      refetch();

      handleClose();
      reset();
    }
  };

  return (
    <>
      <Modal
        isOpen={open}
        onRequestClose={handleClose}
        style={{
          overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            color: 'inherit',
            backgroundColor: 'rgba(25, 25, 25, 0.75)',
          },
          content: {
            width: '400px',
            overflow: 'unset',
            position: 'absolute',
            margin: '0.1% auto',
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            padding: 0,
            marginRight: '-50%',
            transform: 'translate(-50%, -48vh)',
            border: '0px',
            background: 'transparent',
            WebkitOverflowScrolling: 'touch',
            borderRadius: '4px',
            outline: 'none',
          },
        }}>
        <Box
          sx={{
            backgroundColor: '#fff',
            overflow: 'unset',
            borderRadius: '5px',
          }}
          mt={9}>
          <Box sx={{ px: 5, py: 2 }} alignItems={'center'}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography gutterBottom variant="subtitle2" m={0}>
                Edit Cashier Password
              </Typography>
              <EditNoteIcon />
            </Stack>
          </Box>
          <Divider />

          <Box px={5} pt={2} pb={2}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid>
                <Controller
                  name="newPassword"
                  control={control}
                  rules={{
                    required: 'First name is required',
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="New Password"
                      type="password"
                      variant="standard"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error ? fieldState.error.message : null}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ mt: 5 }}
                    />
                  )}
                />

                <Controller
                  name="confirmPassword"
                  control={control}
                  rules={{
                    required: 'Confirm password is required',
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Confirm Password"
                      type="password"
                      variant="standard"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error ? fieldState.error.message : null}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ mt: 5 }}
                    />
                  )}
                />
                {errorMessage !== '' && (
                  <FormHelperText error sx={{ mt: 1 }}>
                    {errorMessage}
                  </FormHelperText>
                )}
              </Grid>
              <Grid container justifyContent={'end'} mt={5}>
                <Button
                  type="submit"
                  size="small"
                  variant="contained"
                  sx={{
                    background: 'grey',
                    fontSize: 10,
                    fontWeight: 600,
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    textTransform: 'unset',
                  }}
                  onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  sx={{
                    ml: 1,
                    fontSize: 10,
                    fontWeight: 600,
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    textTransform: 'unset',
                  }}
                  type="submit"
                  size="small"
                  variant="contained">
                  Submit
                </Button>
              </Grid>
            </form>
          </Box>
        </Box>
      </Modal>
    </>
  );
};
